import { useI18n } from '@ecom/i18n';
import { Button } from '@ecom/ui-kit';
import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';

/**
 * The onboarding guidance the supervisor's UX remark asked for: after the
 * FIRST successful save of a step (store created, first category created),
 * point at the next one rather than leaving the merchant to discover the
 * sidebar on their own. Deliberately not a `Toast` — a toast auto-dismisses
 * after a few seconds (`Toast.tsx`'s own default), which is right for "your
 * change was saved" but wrong for a call to action the merchant should
 * still see if they look back up a minute later. This stays on the page
 * until the merchant dismisses it or navigates away.
 *
 * Purely a client-side presentation choice: it reads props the caller
 * already has (nothing new fetched), links to a route the merchant already
 * has permission to reach on their own via the sidebar, and calls no API of
 * its own — no interaction with RBAC or audit logging, because it
 * performs no privileged action itself.
 */
export function NextStepBanner({
  title,
  description,
  ctaLabel,
  to,
  onDismiss,
}: {
  readonly title: string;
  readonly description: string;
  readonly ctaLabel: string;
  readonly to: string;
  readonly onDismiss: () => void;
}): ReactElement {
  const { t } = useI18n();

  return (
    <div
      role="status"
      className="flex flex-col gap-3 rounded-md border border-good bg-goodSoft p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <p className="font-display text-sm font-semibold text-good">✅ {title}</p>
        <p className="text-sm text-ink">{description}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Link to={to}>
          <Button variant="secondary">{ctaLabel}</Button>
        </Link>
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('common.close')}
          className="text-sm text-inkFaint underline"
        >
          {t('common.close')}
        </button>
      </div>
    </div>
  );
}
